import React, { useState } from 'react';
import './SenseJS.scss';
import { parseJwt } from '../../../views/login/Login';
import { BASE_URL } from '../../../../config/environmentConfig';
import { useTranslation } from 'react-i18next';
import { ScriptComponent } from '../ScriptComponent';
import { useParams } from 'react-router-dom';
import { Title } from '../page-components/Title';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fa1, fa2, fa3, faChevronLeft, faChevronRight, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Timeline } from 'rsuite';
import { BUTTON_TYPE, ButtonComponent } from '../../ButtonComponent';
import { IModalWindow, ModalWindow } from '../../modal/ModalWindow';
import { SenseJsVerificationModal } from './sense-js-modals/SenseJsVerificationModal';
import { SenseJsModal } from './sense-js-modals/SenseJsModal';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';

export const getSenseScript = (mid, siteId, siteName = ''): string => (
	`
<script>
	// Site Name: ${siteName}

    var scriptId = 'fc';
    var scriptSrc = '${BASE_URL.SENSE_JS_URL}';
    var MID = '${mid}';
    var siteId = '${siteId}';
    
    (function (w,d,s,o,f,js,fjs) {
        w['JS-Widget']=o;w[o] = w[o] || function () { (w[o].q = w[o].q || []).push(arguments) };
        js = d.createElement(s), fjs = d.getElementsByTagName(s)[0];
        js.id = o; js.src = f; js.async = 1; fjs.parentNode.insertBefore(js, fjs);
    }(window, document, 'script', scriptId, scriptSrc));
    fc('init', MID, siteId);
</script>
`
);

export enum REQUEST_RESULT {
	SUCCESS = 'success',
	TRACKING_CODE = 'trackingCode',
	NOT_FOUND = 'NOT_FOUND',
	SITE_NOT_FOUND = 'SITE_NOT_FOUND',
	SITE_CAN_NOT_REACH = 'SITE_CAN_NOT_REACH',

}

export const SenseJS = (): JSX.Element => {
	const { merchantState: { senseJSValidationResult }} = useMerchants();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const isMerchant = checkRoles(authUser, CLAIMS.MERCHANT_ADMIN);
	const idToken = localStorage.getItem('idToken');
	const parsedToken = parseJwt(idToken);
	const mid = parsedToken['custom:mid'];
	const { t } = useTranslation();
	const { id, siteId } = useParams<{ id: string, siteId: string }>();
	const merchantId = isMerchant ? mid : id;
	const siteID = isMerchant ? id : siteId;
	const [siteName, setSiteName] = useState<string>('');
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const [isVerifyModalOpen, setIsVerifyModalOpen] = useState<boolean>(false);

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const responseModal = (status: REQUEST_RESULT): void => {
		const errorTitle = (
			<div>
				<FontAwesomeIcon icon={faTriangleExclamation} className="me-2 w-10px"/>
				<span className="fs-8">{siteName} has not reported any  data in the past 24 hours</span>
			</div>
		);
		setModalState({
			isOpen: true,
			onClose: closeModal,
			contentClassName: 'd-block',
			backdrop: 'static',
			errorModal: status !== REQUEST_RESULT.SUCCESS,
			errorModalTitle: status === REQUEST_RESULT.SUCCESS ? '' : errorTitle,
			content:
				<SenseJsModal
					requestResult={status}
					siteId={siteID}
					siteName={siteName}
					closeModal={closeModal}
					openVerificationModal={(): void => setIsVerifyModalOpen(true)}
				/>
		});
	};

	const senseJsBodyScript = `
<script>
   window.addEventListener('load', function() {
      	window.fc.createOrderId(); //FlexCharge will generate an orderId for you
   });
 </script>
	`;

	return(
		<div className="card">
				<div className="d-flex align-items-center mb-3 mt-7 ms-5">
					<div className="me-5">
						<FontAwesomeIcon icon={faChevronLeft} width={17} className="h-17px text-danger"/>
						<FontAwesomeIcon icon={faChevronRight} width={17} className="h-17px text-danger"/>
					</div>
					<Title title="To rescue transactions, install Flex on your checkout" additionalClass="fw-bolder fs-4"/>
				</div>
			<div className="card-body pt-3">
				<Timeline className="sense-js-timeline">
					<Timeline.Item dot={<FontAwesomeIcon icon={fa1}/>}>
						<div className="text-muted fs-8 mb-2 pt-1">Embed this script in the header of your test page</div>
						<ScriptComponent
							script={getSenseScript(merchantId, siteID, siteName)}
							copy
						/>
					</Timeline.Item>
					<Timeline.Item dot={<FontAwesomeIcon icon={fa2}/>}>
						<div className="text-muted fs-8 mb-2 pt-1">Invoke this script in your the body of your test page</div>
						<ScriptComponent
							script={senseJsBodyScript}
							copy
						/>
					</Timeline.Item>
					<Timeline.Item dot={<FontAwesomeIcon icon={fa3}/>}>
						<div className="d-flex align-items-center position-relative sense-js-position">
							<div className="text-muted fs-8 me-5">Verify your code is was installed</div>
							<ButtonComponent
								buttonName="Verify installation"
								buttonType={BUTTON_TYPE.WHITE}
								additionalClass="btn-sm px-3 py-2"
								onButtonClick={(): void => setIsVerifyModalOpen(true)}
							/>
						</div>
					</Timeline.Item>
				</Timeline>
			</div>
			<ModalWindow
				{ ...modalState }
				onClose={closeModal}
			/>
			<ModalWindow
				isOpen={isVerifyModalOpen}
				onClose={(): void => setIsVerifyModalOpen(false)}
				modalTitle='Verify your installation'
				contentClassName="d-block"
				backdrop="static"
				content={
					<SenseJsVerificationModal
						siteId={siteID}
						mid={merchantId}
						responseModal={responseModal}
						closeModal={(): void => setIsVerifyModalOpen(false)}
						setSiteName={setSiteName}
					/>
				}
			/>
		</div>
	);
};
